import React, { useState } from 'react';
import api from '../services/api.ts';
import { useParams, useNavigate } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from 'react-query';
import { AircraftsAPI, Aircraft, AircraftStatus } from '../services/aircrafts.ts';
import { formatCurrency } from '../utils/currency.ts';
import { formatStatus, getStatusColor } from '../utils/statusFormatter.ts';
import ConfirmationModal from '../components/ConfirmationModal.tsx';
import {
  ArrowLeftIcon,
  PaperAirplaneIcon,
  CurrencyDollarIcon,
  PencilIcon,
  TrashIcon,
  CheckCircleIcon,
  XCircleIcon,
  ClockIcon,
  CalendarIcon,
  TagIcon,
  DocumentTextIcon,
  PhotoIcon,
  ArrowDownTrayIcon,
  EyeIcon,
} from '@heroicons/react/24/outline';
import ImageGallery from '../components/ImageGallery.tsx';

// Função utilitária para construir URLs do backend
const getBackendUrl = (path: string) => {
  const baseUrl = process.env.REACT_APP_API_URL || 'http://localhost:8080';
  return `${baseUrl}${path}`;
};

const AircraftDetail: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const queryClient = useQueryClient();

  const [activeTab, setActiveTab] = useState<'details' | 'history' | 'related' | 'attachments'>('details');
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [showImageGallery, setShowImageGallery] = useState(false);
  const [galleryStartIndex, setGalleryStartIndex] = useState(0);

  const { data: aircraft, isLoading, error } = useQuery<Aircraft>(
    ['aircraft', id],
    async () => {
      const response = await AircraftsAPI.get(Number(id));
      
      // Processar attachments se existirem
      if (response.attachments && Array.isArray(response.attachments)) {
        response.attachments = response.attachments.map((attachment: any) => {
          // Garantir que o type exista, derivando do mimetype se necessário
          const type = attachment.type || (attachment.mimetype?.startsWith('image/') ? 'image' : 'document');
          
          return {
            ...attachment,
            type,
            preview:
              type === 'image' && attachment.url ? getBackendUrl(attachment.url) : undefined,
            file: new File([], attachment.name || 'file'),
          };
        });
      } else {
        response.attachments = [];
      }
      
      return response;
    },
    { enabled: !!id }
  );

  const { data: history, isLoading: historyLoading } = useQuery(
    ['aircraft-history', id],
    async () => AircraftsAPI.history(Number(id)),
    { enabled: !!id && activeTab === 'history' }
  );

  const { data: relatedAssets } = useQuery<any[]>(
    ['related-aircraft', id],
    async () => {
      if (!id) return [];
      const response = await api.get(`/aircrafts/${id}/related`);
      return response.data;
    },
    { enabled: !!id }
  );

  const { data: assetRelatedAssets } = useQuery<any[]>(
    ['related-assets', aircraft?.assetId],
    async () => {
      if (!aircraft?.assetId) return [];
      const response = await api.get(`/assets/${aircraft.assetId}/related`);
      return response.data;
    },
    { enabled: !!aircraft?.assetId }
  );

  const deleteMutation = useMutation(
    async () => AircraftsAPI.delete(Number(id)),
    {
      onSuccess: () => {
        queryClient.invalidateQueries(['aircrafts']);
        navigate('/aircrafts');
      },
      onError: (error: any) => {
        console.error('Erro ao deletar aeronave:', error);
      },
    }
  );

  if (isLoading) {
    return (
      <div className="max-w-4xl mx-auto">
        <div className="bg-white shadow rounded-lg">
          <div className="px-4 py-5 sm:p-6">
            <div className="animate-pulse">
              <div className="h-6 bg-gray-200 rounded w-1/4 mb-4"></div>
              <div className="space-y-4">
                <div className="h-4 bg-gray-200 rounded w-full"></div>
                <div className="h-4 bg-gray-200 rounded w-3/4"></div>
                <div className="h-4 bg-gray-200 rounded w-1/2"></div>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  if (error || !aircraft) {
    return (
      <div className="max-w-4xl mx-auto">
        <div className="bg-white shadow rounded-lg">
          <div className="px-4 py-5 sm:p-6">
            <div className="text-center py-8">
              <p className="text-red-600">Erro ao carregar os dados da aeronave.</p>
            </div>
          </div>
        </div>
      </div>
    );
  }

  const handleEdit = () => navigate(`/edit-aircraft/${id}`);
  const handleSell = () => navigate(`/aircrafts/${id}/sale`);
  const handleDelete = () => {
    deleteMutation.mutate();
    setShowDeleteModal(false);
  };

  const getStatusIcon = (status: AircraftStatus | string) => {
    switch (status) {
      case AircraftStatus.IN_STOCK:
      case 'in_stock':
        return <CheckCircleIcon className="h-5 w-5" />;
      case AircraftStatus.SOLD:
      case 'sold':
        return <XCircleIcon className="h-5 w-5" />;
      case AircraftStatus.RESERVED:
      case 'reserved':
        return <ClockIcon className="h-5 w-5" />;
      default:
        return <DocumentTextIcon className="h-5 w-5" />;
    }
  };

  const getActionLabel = (action: string) => {
    const a = String(action).toUpperCase();
    switch (a) {
      case 'CREATE':
        return 'Criação';
      case 'UPDATE':
        return 'Atualização';
      case 'SALE':
        return 'Venda';
      case 'DELETE':
        return 'Exclusão';
      default:
        return action;
    }
  };

  const getFieldLabel = (key: string) => {
    const map: Record<string, string> = {
      make: 'Fabricante',
      model: 'Modelo',
      year: 'Ano',
      tailNumber: 'Prefixo',
      color: 'Cor',
      status: 'Status',
      purchasePrice: 'Preço de Compra',
      sellingPrice: 'Preço de Venda',
      purchaseDate: 'Data de Aquisição',
      soldDate: 'Data de Venda',
      category: 'Categoria',
      assetId: 'Asset ID',
    };
    return map[key] ?? key;
  };

  const getCategoryLabel = (c?: string | null) => {
    if (!c) return '—';
    switch (c) {
      case 'airplane':
        return 'Avião';
      case 'helicopter':
        return 'Helicóptero';
      case 'other':
        return 'Outros';
      default:
        return String(c);
    }
  };

  const isSameDateOnly = (a: any, b: any) => {
    const da = new Date(a);
    const db = new Date(b);
    if (isNaN(da.getTime()) || isNaN(db.getTime())) {
      return String(a) === String(b);
    }
    return (
      da.getFullYear() === db.getFullYear() &&
      da.getMonth() === db.getMonth() &&
      da.getDate() === db.getDate()
    );
  };

  const shouldIgnoreChange = (key: string, change: any) => {
    if (key === 'createdAt' || key === 'updatedAt') return true;
    if (key === 'purchaseDate' || key === 'soldDate') {
      return isSameDateOnly(change?.old, change?.new);
    }
    return change?.old === change?.new;
  };

  const formatValueByKey = (key: string, value: any) => {
    if (value === null || value === undefined || value === '') return '—';
    if (key === 'purchasePrice' || key === 'sellingPrice') {
      const num = Number(value) || 0;
      return formatCurrency(num);
    }
    if (key === 'purchaseDate' || key === 'soldDate') {
      const d = new Date(value);
      return isNaN(d.getTime()) ? String(value) : d.toLocaleDateString();
    }
    if (key === 'createdAt' || key === 'updatedAt') {
      const d = new Date(value);
      return isNaN(d.getTime()) ? String(value) : d.toLocaleString();
    }
    if (key === 'status') {
      return formatStatus(String(value));
    }
    if (key === 'category') {
      return getCategoryLabel(String(value));
    }
    return String(value);
  };

  const displayRelatedAssets = (relatedAssets && relatedAssets.length > 0) ? relatedAssets : (assetRelatedAssets || []);

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-50 to-gray-100 py-8">
      <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Header */}
        <div className="mb-8">
          <button
            onClick={() => navigate('/aircrafts')}
            className="flex items-center text-gray-600 hover:text-gray-900 mb-4 transition-colors"
          >
            <ArrowLeftIcon className="h-5 w-5 mr-2" />
            Voltar para Aeronaves
          </button>

          <div className="flex items-center justify-between">
            <div>
              <h1 className="text-3xl font-bold text-gray-900">
                {aircraft.make} {aircraft.model} ({aircraft.year})
              </h1>
              <p className="text-gray-600 mt-2">Prefixo: {aircraft.tailNumber}</p>
              {aircraft.category && (
                <div className="mt-2">
                  <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-gray-100 text-gray-700">
                    {aircraft.category === 'airplane' ? 'Avião' : aircraft.category === 'helicopter' ? 'Helicóptero' : 'Outros'}
                  </span>
                </div>
              )}
            </div>

            <div className="flex items-center space-x-3">
              <div className={`inline-flex items-center px-3 py-1 rounded-full text-sm font-medium ${getStatusColor(aircraft.status)}`}>
                {getStatusIcon(aircraft.status)}
                <span className="ml-2">{formatStatus(aircraft.status)}</span>
              </div>
            </div>
          </div>
        </div>

        {/* Tabs */}
        <div className="bg-white rounded-2xl border border-gray-100 shadow-sm mb-8">
          <div className="px-6 py-4 border-b border-gray-100">
            <nav className="-mb-px flex space-x-8">
              <button
                onClick={() => setActiveTab('details')}
                className={`py-2 px-1 border-b-2 font-medium text-sm ${
                  activeTab === 'details'
                    ? 'border-primary-500 text-primary-600'
                    : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                }`}
              >
                Detalhes
              </button>
              <button
                onClick={() => setActiveTab('history')}
                className={`py-2 px-1 border-b-2 font-medium text-sm ${
                  activeTab === 'history'
                    ? 'border-primary-500 text-primary-600'
                    : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                }`}
              >
                Histórico
              </button>
              <button
                onClick={() => setActiveTab('related')}
                className={`py-2 px-1 border-b-2 font-medium text-sm ${
                  activeTab === 'related'
                    ? 'border-primary-500 text-primary-600'
                    : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                }`}
              >
                Ativos Relacionados
              </button>
              <button
                onClick={() => setActiveTab('attachments')}
                className={`py-2 px-1 border-b-2 font-medium text-sm ${
                  activeTab === 'attachments'
                    ? 'border-primary-500 text-primary-600'
                    : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                }`}
              >
                Anexos
              </button>
            </nav>
          </div>
        </div>

        {/* Content */}
        {activeTab === 'details' && (
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
            {/* Informações Financeiras */}
            <div className="lg:col-span-2 space-y-6">
              <div className="bg-white rounded-2xl border border-gray-100 shadow-sm p-6">
                <h3 className="text-xl font-semibold text-gray-900 mb-6">Informações Financeiras</h3>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">Preço de Compra</label>
                    <div className="flex items-center">
                      <CurrencyDollarIcon className="h-5 w-5 text-gray-400 mr-2" />
                      <span className="text-lg font-semibold text-gray-900">{formatCurrency(Number(aircraft.purchasePrice || 0))}</span>
                    </div>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">Preço de Venda</label>
                    <div className="flex items-center">
                      <CurrencyDollarIcon className="h-5 w-5 text-gray-400 mr-2" />
                      <span className="text-lg font-semibold text-gray-900">{aircraft.sellingPrice ? formatCurrency(Number(aircraft.sellingPrice)) : '-'}</span>
                    </div>
                  </div>
                </div>
              </div>

              {/* Detalhes */}
              <div className="bg-white rounded-2xl border border-gray-100 shadow-sm p-6">
                <h3 className="text-xl font-semibold text-gray-900 mb-6">Detalhes</h3>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  {aircraft.category && (
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">Categoria</label>
                      <div className="text-gray-900">{aircraft.category === 'airplane' ? 'Avião' : aircraft.category === 'helicopter' ? 'Helicóptero' : 'Outros'}</div>
                    </div>
                  )}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">Marca</label>
                    <div className="text-gray-900">{aircraft.make}</div>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">Modelo</label>
                    <div className="text-gray-900">{aircraft.model}</div>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">Ano</label>
                    <div className="text-gray-900">{aircraft.year}</div>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">Prefixo</label>
                    <div className="text-gray-900">{aircraft.tailNumber}</div>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">Cor</label>
                    <div className="text-gray-900">{aircraft.color}</div>
                  </div>
                  {aircraft.notes && (
                    <div className="md:col-span-2">
                      <label className="block text-sm font-medium text-gray-700 mb-2">Observação</label>
                      <div className="text-gray-900 whitespace-pre-wrap">{aircraft.notes}</div>
                    </div>
                  )}
                </div>
              </div>
            </div>

            {/* Ações */}
            <div className="space-y-6">
              <div className="bg-white rounded-2xl border border-gray-100 shadow-sm p-6">
                <h3 className="text-lg font-semibold text-gray-900 mb-4">Ações</h3>
                <div className="flex flex-col space-y-3">
                  <button onClick={handleEdit} className="bg-blue-600 hover:bg-blue-700 text-white px-4 py-2 rounded-lg flex items-center justify-center">
                    <PencilIcon className="h-5 w-5 mr-2" /> Editar Aeronave
                  </button>
                  {aircraft.status === AircraftStatus.IN_STOCK && (
                    <button onClick={() => navigate(`/exchanges/new?originType=aircraft&originId=${id}`)} className="bg-purple-600 hover:bg-purple-700 text-white px-4 py-2 rounded-lg flex items-center justify-center">
                      Registrar permuta
                    </button>
                  )}
                  {aircraft.status === AircraftStatus.IN_STOCK && (
                    <button onClick={handleSell} className="bg-green-600 hover:bg-green-700 text-white px-4 py-2 rounded-lg flex items-center justify-center">
                      <TagIcon className="h-5 w-5 mr-2" /> Registrar Venda
                    </button>
                  )}
                  <button onClick={() => setShowDeleteModal(true)} className="bg-red-600 hover:bg-red-700 text-white px-4 py-2 rounded-lg flex items-center justify-center">
                    <TrashIcon className="h-5 w-5 mr-2" /> Excluir Aeronave
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}

          {activeTab === 'related' && (
              <div className="lg:col-span-3 space-y-6">
          <div className="bg-white rounded-2xl border border-gray-100 shadow-sm p-6">
            <h3 className="text-xl font-semibold text-gray-900 mb-6">Ativos Relacionados</h3>
            {((relatedAssets && relatedAssets.length > 0 ? relatedAssets : assetRelatedAssets) || [])?.length > 0 ? (
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {((relatedAssets && relatedAssets.length > 0 ? relatedAssets : assetRelatedAssets) || [])?.map((relatedAsset: any) => {
                  const getRelationshipLabel = () => {
                    if (relatedAsset.relationship === 'exchange_origin') return 'Cedidos na permuta';
                    if (relatedAsset.relationship === 'exchange_result') return 'Recebidos na permuta';
                    return 'Relacionado';
                  };
                  const getTypeLabel = () => {
                    if (relatedAsset.type === 'vehicle') return 'Veículo';
                    if (relatedAsset.type === 'aircraft') return 'Aeronave';
                    if (relatedAsset.type === 'vessel') return 'Embarcação';
                    if (relatedAsset.type === 'real_estate') return 'Imóvel';
                    return 'Patrimônio';
                  };
                  return (
                    <div key={relatedAsset.id} className="border border-gray-200 rounded-lg p-4">
                      <div className="flex items-center justify-between mb-2">
                        <h4 className="font-medium text-gray-900">{relatedAsset.description}</h4>
                        <span className={`px-2 py-1 rounded-full text-xs font-medium ${getStatusColor(relatedAsset.status)}`}>
                          {formatStatus(relatedAsset.status)}
                        </span>
                      </div>
                      <p className="text-xs text-gray-500 mb-2">Tipo: {getTypeLabel()}</p>
                      <p className="text-sm text-gray-600 mb-2">Valor: {formatCurrency(Number(relatedAsset.currentValue || 0))}</p>
                      <div className="flex items-center justify-between">
                        <p className="text-xs text-gray-500">Relacionamento: {getRelationshipLabel()}</p>
                        <button
                          onClick={() => {
                            if (relatedAsset.type === 'vehicle') navigate(`/vehicles/${relatedAsset.id}`);
                            else if (relatedAsset.type === 'aircraft') navigate(`/aircrafts/${relatedAsset.id}`);
                            else if (relatedAsset.type === 'vessel') navigate(`/vessels/${relatedAsset.id}`);
                            else navigate(`/assets/${relatedAsset.id}`);
                          }}
                          className="text-primary-600 hover:text-primary-800 text-sm font-medium flex items-center"
                        >
                          <DocumentTextIcon className="h-4 w-4 mr-1" /> Ver
                        </button>
                      </div>
                    </div>
                  );
                })}
              </div>
            ) : (
              <div className="text-gray-600">Nenhum ativo relacionado encontrado.</div>
            )}
          </div>
              </div>
            )}

        {activeTab === 'attachments' && (
          <div className="bg-white rounded-2xl border border-gray-100 shadow-sm p-6">
            <div className="flex items-center justify-between mb-6">
              <h3 className="text-xl font-semibold text-gray-900">Anexos - Fotos e Documentos</h3>
              <div className="flex items-center space-x-4">
                <span className="text-sm text-gray-500">
                  {aircraft.attachments?.length || 0} arquivo(s)
                </span>
                <button
                  onClick={() => navigate(`/edit-aircraft/${id}`)}
                  className="text-primary-600 hover:text-primary-800 text-sm font-medium"
                >
                  Adicionar/Editar
                </button>
              </div>
            </div>

            {/* Galeria de Fotos */}
            <div className="mb-8">
              <h4 className="text-sm font-medium text-gray-700 mb-4 flex items-center">
                <PhotoIcon className="h-4 w-4 mr-2" />
                Galeria de Fotos
              </h4>
              {(aircraft.attachments || []).filter((a: any) => a.type === 'image').length > 0 ? (
                <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-6 gap-4">
                  {(aircraft.attachments || [])
                    .filter((a: any) => a.type === 'image')
                    .map((photo: any, index: number) => (
                      <div
                        key={photo.id}
                        className="group relative aspect-square rounded-lg overflow-hidden bg-gray-100 cursor-pointer"
                        onClick={() => {
                          setGalleryStartIndex(index);
                          setShowImageGallery(true);
                        }}
                        role="button"
                        tabIndex={0}
                        onKeyDown={(e) => {
                          if (e.key === 'Enter' || e.key === ' ') {
                            setGalleryStartIndex(index);
                            setShowImageGallery(true);
                          }
                        }}
                      >
                        <img
                          src={photo.preview || (photo.url ? getBackendUrl(photo.url) : '')}
                          alt={photo.name}
                          className="w-full h-full object-cover transition-transform duration-300 group-hover:scale-110"
                        />
                        <div className="absolute inset-0 bg-black bg-opacity-0 group-hover:bg-opacity-30 transition-opacity flex items-center justify-center">
                          <EyeIcon className="h-8 w-8 text-white opacity-0 group-hover:opacity-100 transition-opacity" />
                        </div>
                      </div>
                    ))}
                </div>
              ) : (
                <p className="text-sm text-gray-500 italic">Nenhuma foto adicionada.</p>
              )}
            </div>

            {/* Lista de Documentos */}
            <div>
              <h4 className="text-sm font-medium text-gray-700 mb-4 flex items-center">
                <DocumentTextIcon className="h-4 w-4 mr-2" />
                Documentos
              </h4>
              {(aircraft.attachments || []).filter((a: any) => a.type === 'document').length > 0 ? (
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                  {(aircraft.attachments || [])
                    .filter((a: any) => a.type === 'document')
                    .map((doc: any) => (
                      <div
                        key={doc.id}
                        className="flex items-center p-4 border border-gray-200 rounded-lg hover:border-primary-300 transition-colors bg-gray-50"
                      >
                        <div className="flex-shrink-0">
                          <DocumentTextIcon className="h-8 w-8 text-gray-400" />
                        </div>
                        <div className="ml-4 flex-1 min-w-0">
                          <p className="text-sm font-medium text-gray-900 truncate" title={doc.name}>
                            {doc.name}
                          </p>
                          <p className="text-xs text-gray-500">
                            {(doc.size / 1024).toFixed(0)} KB •{' '}
                            {new Date().toLocaleDateString('pt-BR')}
                          </p>
                        </div>
                        <div className="ml-4 flex-shrink-0">
                          <button
                            onClick={() => {
                              if (doc.url) {
                                const link = document.createElement('a');
                                link.href = getBackendUrl(doc.url);
                                link.download = doc.name;
                                link.target = '_blank';
                                link.click();
                              }
                            }}
                            className="text-gray-400 hover:text-primary-600 transition-colors"
                            title="Baixar documento"
                          >
                            <ArrowDownTrayIcon className="h-5 w-5" />
                          </button>
                        </div>
                      </div>
                    ))}
                </div>
              ) : (
                <p className="text-sm text-gray-500 italic">Nenhum documento adicionado.</p>
              )}
            </div>

            {showImageGallery && (
              <ImageGallery
                images={(aircraft.attachments || []).filter((a: any) => a.type === 'image')}
                onClose={() => setShowImageGallery(false)}
                initialIndex={galleryStartIndex}
              />
            )}
          </div>
        )}

        {activeTab === 'history' && (
          <div className="bg-white rounded-2xl border border-gray-100 shadow-sm p-6">
            <h3 className="text-xl font-semibold text-gray-900 mb-6">Histórico</h3>
            {historyLoading ? (
              <div className="text-gray-500">Carregando histórico...</div>
            ) : (
              <div className="space-y-4">
                {history?.map((h: any) => {
                  const d: any = h.details || {};

                  const renderUpdate = () => {
                    const changes = d.changes || {};
                    const entries = Object.entries(changes).filter(([key, change]) => !shouldIgnoreChange(key, change));
                    if (!entries.length) return <div className="text-sm text-gray-500">Nenhuma alteração registrada.</div>;
                    return (
                      <div className="mt-2 space-y-2">
                        {entries.map(([key, change]: [string, any]) => (
                          <div key={key} className="text-sm">
                            <span className="font-medium text-gray-900">{getFieldLabel(key)}: </span>
                            <span className="text-gray-700">{formatValueByKey(key, change?.old)}</span>
                            <span className="mx-1 text-gray-400">→</span>
                            <span className="text-gray-700">{formatValueByKey(key, change?.new)}</span>
                          </div>
                        ))}
                      </div>
                    );
                  };

                  const renderCreate = () => {
                    const data = d.aircraftData || {};
                    const keys = ['make', 'model', 'year', 'tailNumber', 'color', 'status', 'purchasePrice', 'category'];
                    return (
                      <div className="mt-2 grid grid-cols-1 gap-2 sm:grid-cols-2">
                        {keys.map((key) => (
                          <div key={key} className="text-sm">
                            <span className="font-medium text-gray-900">{getFieldLabel(key)}: </span>
                            <span className="text-gray-700">{formatValueByKey(key, (data as any)[key])}</span>
                          </div>
                        ))}
                      </div>
                    );
                  };

                  const renderSale = () => {
                    const sale = d.saleData || d;
                    const price = formatValueByKey('sellingPrice', sale?.sellingPrice);
                    const when = formatValueByKey('soldDate', sale?.soldDate || sale?.timestamp);
                    return (
                      <div className="mt-2 space-y-2 text-sm">
                        <div>
                          <span className="font-medium text-gray-900">Preço de Venda: </span>
                          <span className="text-gray-700">{price}</span>
                        </div>
                        <div>
                          <span className="font-medium text-gray-900">Data da Venda: </span>
                          <span className="text-gray-700">{when}</span>
                        </div>
                      </div>
                    );
                  };

                  const renderDelete = () => {
                    const data = d.deletedAircraftData || {};
                    return (
                      <div className="mt-2 space-y-2 text-sm">
                        <div>
                          <span className="font-medium text-gray-900">Registro removido.</span>
                        </div>
                        {'tailNumber' in data && (
                          <div>
                            <span className="font-medium text-gray-900">Prefixo: </span>
                            <span className="text-gray-700">{formatValueByKey('tailNumber', (data as any).tailNumber)}</span>
                          </div>
                        )}
                      </div>
                    );
                  };

                  const content =
                    h.action === 'UPDATE'
                      ? renderUpdate()
                      : h.action === 'CREATE'
                      ? renderCreate()
                      : h.action === 'SALE'
                      ? renderSale()
                      : h.action === 'DELETE'
                      ? renderDelete()
                      : (
                          <pre className="mt-2 whitespace-pre-wrap break-words text-sm text-gray-700 bg-gray-50 p-2 rounded">
                            {JSON.stringify(h.details, null, 2)}
                          </pre>
                        );

                  return (
                    <div key={h.id} className="border border-gray-100 rounded-lg p-4">
                      <div className="flex items-center justify-between">
                        <div className="text-sm text-gray-600">{new Date(h.createdAt).toLocaleString()}</div>
                        <div>
                          <span className={`inline-flex items-center rounded-full px-2.5 py-0.5 text-xs font-medium ${
                            h.action === 'UPDATE'
                              ? 'bg-yellow-100 text-yellow-800'
                              : h.action === 'SALE'
                              ? 'bg-green-100 text-green-800'
                              : h.action === 'DELETE'
                              ? 'bg-red-100 text-red-800'
                              : 'bg-blue-100 text-blue-800'
                          }`}>
                            {getActionLabel(h.action)}
                          </span>
                        </div>
                      </div>
                      {content}
                    </div>
                  );
                })}
                {history?.length === 0 && (
                  <div className="text-gray-500">Sem registros de histórico.</div>
                )}
              </div>
            )}
          </div>
        )}

        <ConfirmationModal
          isOpen={showDeleteModal}
          title="Excluir Aeronave"
          message="Tem certeza que deseja excluir esta aeronave? Esta ação não pode ser desfeita."
          onConfirm={handleDelete}
          onClose={() => setShowDeleteModal(false)}
          confirmText="Excluir"
          cancelText="Cancelar"
          type="danger"
          isLoading={deleteMutation.isLoading}
        />
      </div>
    </div>
  );
};

export default AircraftDetail;
